import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { webhooksApi, type CreateWebhookDto, type UpdateWebhookDto } from '@/lib/api/webhooks.api';

export function useWebhooks() {
  return useQuery({
    queryKey: ['webhooks'],
    queryFn: () => webhooksApi.getWebhooks(),
  });
}

export function useWebhook(id: string | undefined) {
  return useQuery({
    queryKey: ['webhooks', id],
    queryFn: () => webhooksApi.getWebhook(id!),
    enabled: !!id,
  });
}

export function useCreateWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWebhookDto) => webhooksApi.createWebhook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
}

export function useUpdateWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWebhookDto }) =>
      webhooksApi.updateWebhook(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
}

export function useDeleteWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => webhooksApi.deleteWebhook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });
}

export function useWebhookDeliveries(webhookId: string | undefined, page = 1, limit = 20) {
  return useQuery({
    queryKey: ['webhook-deliveries', webhookId, page, limit],
    queryFn: () => webhooksApi.getDeliveries(webhookId!, page, limit),
    enabled: !!webhookId,
  });
}

export function useTriggerWebhook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: {
        dataType: 'availability_data' | 'lead_data' | 'shift_data';
        fromDate: string;
        toDate: string;
        confirmedOnly?: boolean;
      };
    }) => webhooksApi.triggerWebhook(id, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['webhook-deliveries', variables.id],
      });
    },
  });
}
